/**
 * Client Reports tab metrics — collected totals, demand volume, status mix,
 * amount by status, and average time spent in each open status.
 */

import {
  PIPELINE_STEPS,
  boardColumnIdForStatus,
  statusDisplayLabel,
} from './matterPipeline.js';
import {
  isPaidStatus,
  matterAmountOwed,
  matterBalanceRemaining,
} from './paidBeforeAssignment.js';
import { listPaymentRecords } from './paymentRecords.js';
import {
  HISTORY_EVENT_TYPES,
  getLocalMatterHistory,
} from './matterHistory.js';
import { formatMatterMoney, matterCurrency } from './matterCurrency.js';
import { activeMatters, archivedMatters } from './bulkDemandActions.js';
import { daysBetween } from './matterKpis.js';
import { fileNamesFromEvidenceUrl } from './evidenceFiles.js';
import {
  attachmentsFromMatter,
  listDemandConversations,
} from './communicationsStore.js';
import { isConversationUnread } from './messagesInbox.js';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Pipeline statuses that count as “open” for dwell-time reporting. */
export const OPEN_PIPELINE_STATUS_IDS = Object.freeze(
  PIPELINE_STEPS.filter((step) => step.id !== 'PAID').map((step) => step.id)
);

export function startOfLocalDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function startOfLocalWeek(date = new Date()) {
  const d = startOfLocalDay(date);
  // Sunday-start week (US).
  d.setDate(d.getDate() - d.getDay());
  return d;
}

export function startOfLocalMonth(date = new Date()) {
  const d = startOfLocalDay(date);
  d.setDate(1);
  return d;
}

export function startOfLocalYear(date = new Date()) {
  const d = startOfLocalDay(date);
  d.setMonth(0, 1);
  return d;
}

function paidAtMs(value) {
  const ms = Date.parse(String(value || ''));
  return Number.isFinite(ms) ? ms : 0;
}

/** Sum payment ledger amounts on/after `sinceMs` (inclusive), grouped by currency. */
export function sumCollectedSince(matters = [], sinceMs = 0, nowMs = Date.now()) {
  const groups = new Map();
  for (const row of listPaymentRecords(matters)) {
    const when = Number(row.paidAtMs) || paidAtMs(row.paidAt);
    if (!when || when < sinceMs || when > nowMs) continue;
    const amount = Number(row.amount) || 0;
    if (!(amount > 0)) continue;
    const currency = String(row.currency || 'USD').toUpperCase() || 'USD';
    groups.set(currency, (groups.get(currency) || 0) + amount);
  }
  return groups;
}

export function formatMoneyGroups(groups, fallback = 'USD') {
  if (!(groups instanceof Map) || !groups.size) {
    return formatMatterMoney(0, fallback);
  }
  return [...groups.entries()]
    .map(([currency, sum]) => formatMatterMoney(sum, currency))
    .join(' · ');
}

export function collectedPeriodTotals(matters = [], now = new Date()) {
  const nowMs = now.getTime();
  const week = sumCollectedSince(matters, startOfLocalWeek(now).getTime(), nowMs);
  const month = sumCollectedSince(matters, startOfLocalMonth(now).getTime(), nowMs);
  const year = sumCollectedSince(matters, startOfLocalYear(now).getTime(), nowMs);
  return {
    week: {
      groups: week,
      label: formatMoneyGroups(week),
      since: startOfLocalWeek(now).toISOString(),
    },
    month: {
      groups: month,
      label: formatMoneyGroups(month),
      since: startOfLocalMonth(now).toISOString(),
    },
    year: {
      groups: year,
      label: formatMoneyGroups(year),
      since: startOfLocalYear(now).toISOString(),
    },
  };
}

export function demandVolumeCounts(allMatters = []) {
  const active = activeMatters(allMatters);
  const archived = archivedMatters(allMatters);
  const open = active.filter((m) => !isPaidStatus(m?.Status));
  const closed = (allMatters || []).filter((m) => isPaidStatus(m?.Status));
  return {
    open: open.length,
    closed: closed.length,
    archived: archived.length,
    combined: (allMatters || []).length,
  };
}

export function demandsByStatus(allMatters = []) {
  const buckets = new Map(PIPELINE_STEPS.map((step) => [step.id, []]));
  for (const matter of allMatters || []) {
    const id = boardColumnIdForStatus(matter?.Status);
    if (!buckets.has(id)) buckets.set(id, []);
    buckets.get(id).push(matter);
  }
  return PIPELINE_STEPS.map((step) => {
    const list = buckets.get(step.id) || [];
    const amountGroups = new Map();
    for (const matter of list) {
      const amount = Number(matter?.Amount_Owed) || 0;
      if (!Number.isFinite(amount)) continue;
      const currency = matterCurrency(matter);
      amountGroups.set(currency, (amountGroups.get(currency) || 0) + amount);
    }
    return {
      id: step.id,
      label: step.label,
      shortLabel: step.shortLabel,
      count: list.length,
      amountLabel: formatMoneyGroups(amountGroups),
      list,
    };
  });
}

const LABEL_TO_STATUS = Object.freeze(
  Object.fromEntries([
    ...PIPELINE_STEPS.flatMap((step) => [
      [String(step.label).toLowerCase(), step.id],
      [String(step.shortLabel).toLowerCase(), step.id],
      [String(step.id).toLowerCase(), step.id],
    ]),
    ['paid before assign', 'PAID'],
    ['paid before assignment', 'PAID'],
    ['new case', 'INTAKE_VALIDATED'],
  ])
);

/** Map a STATUS_CHANGED summary (or raw status) onto a pipeline column id. */
export function parseStatusFromHistorySummary(summary = '') {
  const text = String(summary || '').trim();
  if (!text) return null;
  const changed = text.match(/status changed to\s+(.+)$/i);
  const token = (changed ? changed[1] : text).trim();
  if (!token) return null;
  const upper = token.toUpperCase().replace(/\s+/g, '_');
  const column = boardColumnIdForStatus(upper);
  if (PIPELINE_STEPS.some((step) => step.id === column) || column === 'PAID') {
    // Prefer known ids; boardColumnIdForStatus falls back to input.
    if (PIPELINE_STEPS.some((step) => step.id === column)) return column;
    if (column === 'PAID') return 'PAID';
  }
  const byLabel = LABEL_TO_STATUS[token.toLowerCase()];
  if (byLabel) return byLabel;
  // Last resort: match display label of current pipeline.
  for (const step of PIPELINE_STEPS) {
    if (statusDisplayLabel(step.id).toLowerCase() === token.toLowerCase()) {
      return step.id;
    }
  }
  return null;
}

/**
 * Build ordered status stays for one demand from history (CREATED + STATUS_CHANGED).
 * Falls back to Created_At → current status when history is thin.
 */
export function statusStaysForMatter(matter, { nowMs = Date.now(), history } = {}) {
  if (!matter?.ROWID) return [];
  const events = Array.isArray(history)
    ? history
    : getLocalMatterHistory(matter.ROWID);

  const timeline = [];
  for (const event of events || []) {
    const type = String(event?.Event_Type || '').toUpperCase();
    const at = Date.parse(String(event?.Created_At || ''));
    if (!Number.isFinite(at)) continue;
    if (type === HISTORY_EVENT_TYPES.CREATED) {
      timeline.push({ at, status: 'INTAKE_VALIDATED' });
      continue;
    }
    if (type === HISTORY_EVENT_TYPES.STATUS_CHANGED) {
      const status = parseStatusFromHistorySummary(event?.Summary);
      if (status) timeline.push({ at, status });
      continue;
    }
    if (type === HISTORY_EVENT_TYPES.ATTORNEY_ASSIGNED) {
      // Assignment implies leaving New Case when no explicit status event exists.
      timeline.push({ at, status: 'ATTORNEY_ASSIGNED' });
    }
  }

  timeline.sort((a, b) => a.at - b.at);

  if (!timeline.length) {
    const created = Date.parse(String(matter.Created_At || ''));
    const start = Number.isFinite(created) ? created : nowMs;
    const status = boardColumnIdForStatus(matter.Status) || 'INTAKE_VALIDATED';
    return [{ status, startedAt: start, endedAt: nowMs, durationMs: Math.max(0, nowMs - start) }];
  }

  // Ensure the first point is New Case at Created_At when missing.
  const created = Date.parse(String(matter.Created_At || ''));
  if (Number.isFinite(created) && timeline[0].at > created) {
    timeline.unshift({ at: created, status: 'INTAKE_VALIDATED' });
  }

  const current = boardColumnIdForStatus(matter.Status) || timeline[timeline.length - 1].status;
  if (timeline[timeline.length - 1].status !== current) {
    timeline.push({ at: nowMs, status: current });
  }

  const stays = [];
  for (let i = 0; i < timeline.length; i += 1) {
    const start = timeline[i];
    const endAt = i + 1 < timeline.length ? timeline[i + 1].at : nowMs;
    if (!(endAt > start.at)) continue;
    // Skip zero-length or immediate duplicate transitions.
    if (stays.length && stays[stays.length - 1].status === start.status && i + 1 < timeline.length) {
      stays[stays.length - 1].endedAt = endAt;
      stays[stays.length - 1].durationMs = Math.max(
        0,
        endAt - stays[stays.length - 1].startedAt
      );
      continue;
    }
    stays.push({
      status: start.status,
      startedAt: start.at,
      endedAt: endAt,
      durationMs: Math.max(0, endAt - start.at),
    });
  }
  return stays;
}

export function formatDurationMs(ms) {
  const value = Math.max(0, Number(ms) || 0);
  if (value < DAY_MS) {
    const hours = value / (60 * 60 * 1000);
    if (hours < 1) {
      const minutes = Math.round(value / (60 * 1000));
      return `${minutes}m`;
    }
    return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`;
  }
  const days = value / DAY_MS;
  if (days < 10) return `${days.toFixed(1)}d`;
  return `${Math.round(days)}d`;
}

/**
 * Average time spent in each open pipeline status across all demands.
 * Paid is excluded (terminal). Includes completed stays and current open stays.
 */
export function averageOpenStatusDurations(allMatters = [], { nowMs = Date.now() } = {}) {
  const totals = new Map(
    OPEN_PIPELINE_STATUS_IDS.map((id) => [id, { totalMs: 0, samples: 0 }])
  );

  for (const matter of allMatters || []) {
    const stays = statusStaysForMatter(matter, { nowMs });
    for (const stay of stays) {
      if (!OPEN_PIPELINE_STATUS_IDS.includes(stay.status)) continue;
      const bucket = totals.get(stay.status);
      if (!bucket) continue;
      bucket.totalMs += stay.durationMs;
      bucket.samples += 1;
    }
  }

  return OPEN_PIPELINE_STATUS_IDS.map((id) => {
    const step = PIPELINE_STEPS.find((s) => s.id === id);
    const bucket = totals.get(id) || { totalMs: 0, samples: 0 };
    const avgMs = bucket.samples ? bucket.totalMs / bucket.samples : 0;
    return {
      id,
      label: step?.label || id,
      shortLabel: step?.shortLabel || id,
      samples: bucket.samples,
      averageMs: avgMs,
      averageLabel: bucket.samples ? formatDurationMs(avgMs) : '—',
    };
  });
}

function amountGroupsForMatters(list = []) {
  const groups = new Map();
  for (const matter of list) {
    const amount = Number(matter?.Amount_Owed) || matterAmountOwed(matter) || 0;
    if (!Number.isFinite(amount)) continue;
    const currency = matterCurrency(matter);
    groups.set(currency, (groups.get(currency) || 0) + amount);
  }
  return groups;
}

function remainingGroupsForMatters(list = []) {
  const groups = new Map();
  for (const matter of list) {
    const amount = matterBalanceRemaining(matter);
    if (!(amount > 0)) continue;
    const currency = matterCurrency(matter);
    groups.set(currency, (groups.get(currency) || 0) + amount);
  }
  return groups;
}

/**
 * Days-open buckets for open demands.
 * DemandJet portfolios are already delinquent when synced, so this is age
 * since Created_At — not a current vs past-due split.
 */
const AGING_BUCKETS = Object.freeze([
  { id: '0-30', label: '0–30 days open', test: (days) => days <= 30 },
  { id: '31-60', label: '31–60 days open', test: (days) => days >= 31 && days <= 60 },
  { id: '61-90', label: '61–90 days open', test: (days) => days >= 61 && days <= 90 },
  { id: '90+', label: '90+ days open', test: (days) => days >= 91 },
]);

/** Open demands by days since Created_At, remaining balance. */
export function agingReport(allMatters = [], { now = new Date() } = {}) {
  const open = activeMatters(allMatters).filter((m) => !isPaidStatus(m?.Status));
  const buckets = AGING_BUCKETS.map((bucket) => ({
    id: bucket.id,
    label: bucket.label,
    count: 0,
    list: [],
  }));

  for (const matter of open) {
    const rawDays = daysBetween(matter.Created_At || matter.createdAt, now);
    // Treat missing/negative as 0 days open (same 0–30 bucket).
    const days = Number.isFinite(rawDays) ? Math.max(0, rawDays) : 0;
    const bucket =
      buckets.find((b) => {
        const def = AGING_BUCKETS.find((d) => d.id === b.id);
        return def?.test(days);
      }) || buckets[0];
    bucket.count += 1;
    bucket.list.push(matter);
  }

  return {
    buckets: buckets.map((b) => ({
      id: b.id,
      label: b.label,
      count: b.count,
      amountLabel: formatMoneyGroups(remainingGroupsForMatters(b.list)),
    })),
    openCount: open.length,
    openAmountLabel: formatMoneyGroups(remainingGroupsForMatters(open)),
  };
}

/** Open demands grouped by attorney partner. */
export function attorneyWorkloadReport(allMatters = []) {
  const open = activeMatters(allMatters).filter((m) => !isPaidStatus(m?.Status));
  const map = new Map();
  for (const matter of open) {
    const name = String(matter?.Attorney_Name || '').trim();
    const id = String(matter?.Attorney_ID || '').trim();
    const key = id || name.toLowerCase() || 'unassigned';
    const label = name || (id ? `Attorney ${id}` : 'Unassigned');
    if (!map.has(key)) {
      map.set(key, { id: key, label, count: 0, list: [] });
    }
    const row = map.get(key);
    row.count += 1;
    row.list.push(matter);
  }
  const rows = [...map.values()]
    .map((row) => ({
      id: row.id,
      label: row.label,
      count: row.count,
      amountLabel: formatMoneyGroups(amountGroupsForMatters(row.list)),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return { rows, openCount: open.length };
}

/** Recent payment ledger activity. */
export function paymentActivityReport(allMatters = [], { now = new Date() } = {}) {
  const records = listPaymentRecords(allMatters);
  const nowMs = now.getTime();
  const weekMs = startOfLocalWeek(now).getTime();
  const monthMs = startOfLocalMonth(now).getTime();
  const week = records.filter((r) => (r.paidAtMs || 0) >= weekMs && (r.paidAtMs || 0) <= nowMs);
  const month = records.filter((r) => (r.paidAtMs || 0) >= monthMs && (r.paidAtMs || 0) <= nowMs);
  const sumLabel = (list) => {
    const groups = new Map();
    for (const row of list) {
      const amount = Number(row.amount) || 0;
      if (!(amount > 0)) continue;
      const currency = String(row.currency || 'USD').toUpperCase() || 'USD';
      groups.set(currency, (groups.get(currency) || 0) + amount);
    }
    return formatMoneyGroups(groups);
  };
  return {
    totalCount: records.length,
    weekCount: week.length,
    weekAmountLabel: sumLabel(week),
    monthCount: month.length,
    monthAmountLabel: sumLabel(month),
    recent: records.slice(0, 25).map((r) => ({
      id: r.id,
      label: `${r.debtorName}${r.demandId ? ` · ${r.demandId}` : ''}`,
      value: r.amountLabel,
      meta: `${r.paidAtLabel} · ${r.kindLabel}`,
    })),
  };
}

/** Average Created_At → Paid_At duration for paid demands. */
export function timeToPaidReport(allMatters = []) {
  const samples = [];
  for (const matter of allMatters || []) {
    if (!isPaidStatus(matter?.Status)) continue;
    const created = Date.parse(String(matter.Created_At || ''));
    const paid = Date.parse(String(matter.Paid_At || matter.Updated_At || ''));
    if (!Number.isFinite(created) || !Number.isFinite(paid) || paid < created) continue;
    samples.push(paid - created);
  }
  const total = samples.reduce((sum, ms) => sum + ms, 0);
  const avgMs = samples.length ? total / samples.length : 0;
  return {
    samples: samples.length,
    averageMs: avgMs,
    averageLabel: samples.length ? formatDurationMs(avgMs) : '—',
  };
}

export const DEFAULT_STALL_DAYS = 14;

/** Open demands with no status change for thresholdDays. */
export function stalledPipelineReport(
  allMatters = [],
  { nowMs = Date.now(), thresholdDays = DEFAULT_STALL_DAYS } = {}
) {
  const thresholdMs = Math.max(1, Number(thresholdDays) || DEFAULT_STALL_DAYS) * DAY_MS;
  const open = activeMatters(allMatters).filter((m) => !isPaidStatus(m?.Status));
  const stalled = [];
  for (const matter of open) {
    const stays = statusStaysForMatter(matter, { nowMs });
    const current = stays[stays.length - 1];
    const startedAt = current?.startedAt || Date.parse(String(matter.Updated_At || matter.Created_At || ''));
    if (!Number.isFinite(startedAt)) continue;
    const idleMs = Math.max(0, nowMs - startedAt);
    if (idleMs < thresholdMs) continue;
    stalled.push({
      matter,
      idleMs,
      status: boardColumnIdForStatus(matter.Status),
      statusLabel: statusDisplayLabel(matter.Status),
    });
  }
  stalled.sort((a, b) => b.idleMs - a.idleMs);
  return {
    thresholdDays: Math.max(1, Number(thresholdDays) || DEFAULT_STALL_DAYS),
    count: stalled.length,
    amountLabel: formatMoneyGroups(amountGroupsForMatters(stalled.map((s) => s.matter))),
    rows: stalled.slice(0, 50).map((s) => ({
      id: String(s.matter.ROWID),
      label: s.statusLabel,
      value: formatDurationMs(s.idleMs),
      meta: String(
        s.matter.Debtor_Name || s.matter.Debtor_Company || s.matter.ROWID || ''
      ),
    })),
  };
}

function matterHasEvidence(matter) {
  const fromUrl = fileNamesFromEvidenceUrl(matter?.Evidence_Vault_URL);
  const fromNames = Array.isArray(matter?.evidenceFileNames)
    ? matter.evidenceFileNames.filter(Boolean)
    : [];
  const attachments = attachmentsFromMatter(matter) || [];
  return fromUrl.length > 0 || fromNames.length > 0 || attachments.length > 0;
}

/** Open demands with no evidence files attached. */
export function evidenceGapsReport(allMatters = []) {
  const open = activeMatters(allMatters).filter((m) => !isPaidStatus(m?.Status));
  const gaps = open.filter((m) => !matterHasEvidence(m));
  return {
    count: gaps.length,
    openCount: open.length,
    amountLabel: formatMoneyGroups(amountGroupsForMatters(gaps)),
  };
}

/** Conversations with unread foreign (attorney/teammate) activity. */
export function messagesNeedingReplyReport(allMatters = [], { viewer = {} } = {}) {
  const threads = listDemandConversations(allMatters);
  const unread = threads.filter((thread) => isConversationUnread(thread, viewer));
  return {
    count: unread.length,
    threadCount: threads.length,
  };
}

/** Demands created this week/month and currently in New Case. */
export function newIntakeReport(allMatters = [], { now = new Date() } = {}) {
  const nowMs = now.getTime();
  const weekMs = startOfLocalWeek(now).getTime();
  const monthMs = startOfLocalMonth(now).getTime();
  const active = activeMatters(allMatters);
  const createdIn = (sinceMs) =>
    active.filter((m) => {
      const created = Date.parse(String(m.Created_At || ''));
      return Number.isFinite(created) && created >= sinceMs && created <= nowMs;
    });
  const week = createdIn(weekMs);
  const month = createdIn(monthMs);
  const newCase = active.filter(
    (m) => boardColumnIdForStatus(m.Status) === 'INTAKE_VALIDATED' && !isPaidStatus(m.Status)
  );
  return {
    weekCount: week.length,
    weekAmountLabel: formatMoneyGroups(amountGroupsForMatters(week)),
    monthCount: month.length,
    monthAmountLabel: formatMoneyGroups(amountGroupsForMatters(month)),
    newCaseCount: newCase.length,
    newCaseAmountLabel: formatMoneyGroups(amountGroupsForMatters(newCase)),
  };
}

/**
 * Full Reports tab snapshot.
 * @param {{ allMatters?: object[], debtors?: object[], now?: Date, viewer?: object }} input
 */
export function buildClientReports({
  allMatters = [],
  debtors = [],
  now = new Date(),
  viewer = {},
} = {}) {
  const collected = collectedPeriodTotals(allMatters, now);
  const volume = demandVolumeCounts(allMatters);
  const byStatus = demandsByStatus(allMatters);
  const avgOpen = averageOpenStatusDurations(allMatters, { nowMs: now.getTime() });

  const byStatusWithAvg = byStatus.map((row) => {
    const avg = avgOpen.find((item) => item.id === row.id);
    return {
      ...row,
      averageOpenLabel: row.id === 'PAID' ? '—' : avg?.averageLabel || '—',
      averageOpenMs: avg?.averageMs || 0,
      averageSamples: avg?.samples || 0,
    };
  });

  return {
    collected,
    volume: {
      ...volume,
      debtors: Array.isArray(debtors) ? debtors.length : 0,
    },
    byStatus: byStatusWithAvg,
    averageOpenStatus: avgOpen,
    aging: agingReport(allMatters, { now }),
    attorney: attorneyWorkloadReport(allMatters),
    payments: paymentActivityReport(allMatters, { now }),
    timeToPaid: timeToPaidReport(allMatters),
    stalled: stalledPipelineReport(allMatters, { nowMs: now.getTime() }),
    evidenceGaps: evidenceGapsReport(allMatters),
    messagesUnread: messagesNeedingReplyReport(allMatters, { viewer }),
    intake: newIntakeReport(allMatters, { now }),
  };
}
